/**
 * BatchRecordTable — the executed batch record as one table (Review by Exception, "Batch record" tab).
 * =================================================================================
 * The same GET /bp/batch/record-view payload BatchRecordView shows as cards, laid out as rows: one row per recorded
 * value, grouped under a header row per operation (number, name, status, start → end, signature count, and the
 * operation's deviations that name no recorded value). Groups collapse; "Show only flagged" keeps the rows out of
 * limits or carrying a deviation; the search box matches the step or the value. A step named by the caller
 * (`focusStep`, an exception's "Show in batch record") is expanded, scrolled to and outlined; its first row carries the
 * card view's anchor (`#record-step-<instance id>`, `data-focused`).
 *
 * The client decides nothing: verdict words are the server's, mapped only to a tag colour (the engine vocabulary's
 * out-of-limit words → "Out of limits"); a deviation attaches to the value row of its operation whose name it carries.
 * A column shows only when some row has something for it. Virtual scrolling keeps a 1 000-row record fast.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Empty, Input, Popover, Space, Switch, Table, Tag, Tooltip, theme } from 'antd';
import { ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown, Search } from 'lucide-react';
import StatusIndicator from '@/components/genealogy/StatusIndicator';
import { formatDateTime } from '@/utils/datetime';
import { statusState } from '@/pages/Batch-Processing/Recipe/BatchProcessDetails/helpers';

const fmt = (v) => (v ? formatDateTime(v) : '');
const joinDot = (parts) => parts.filter(Boolean).join(' · ');
const norm = (s) => String(s ?? '').trim().toLowerCase();
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** Verdict words the engine judges out of limits (engines/vocabulary/cpv.py), as record_view words them. */
const OUT_WORDS = new Set(['out of range', 'ipc fail', 'correct', 'oos', 'fail']);
const verdictKind = (word) => {
  const w = norm(word);
  if (!w) return null;
  if (w === 'in range') return 'within';
  if (OUT_WORDS.has(w)) return 'out';
  return 'other';
};
const capital = (w) => {
  const s = String(w || '').trim().toLowerCase();
  return s ? s[0].toUpperCase() + s.slice(1) : '';
};

const devText = (d) => joinDot([d.type, d.parameter, d.actual ? `actual ${d.actual}` : null, d.limit ? `limit ${d.limit}` : null,
  d.status, d.action, d.raised_by, d.when]);

/**
 * Flatten the operations into groups of rows. A deviation names its parameter; it goes to the value row of the
 * same operation with that name (an out-of-limit one first, then one whose value starts the deviation's actual),
 * otherwise it stays on the operation's header.
 */
const buildGroups = (operations) => operations.map((op, oi) => {
  const opNo = op.operation_number != null ? String(op.operation_number) : String(oi + 1);
  const rows = [];
  (op.steps || []).forEach((st, si) => {
    const stepNo = `${opNo}.${si + 1}`;
    const base = {
      stepId: st.instance_id, stepNo, stepTitle: st.title || '', groupKey: op.batch_config_id,
    };
    const vals = st.values || [];
    const extras = { signatures: st.signatures || [], voided: st.voided || [] };
    if (vals.length === 0) {
      rows.push({ ...base, ...extras, key: `s-${st.instance_id}`, first: true, label: '', value: '', unit: '', limit: '',
        verdict: '', recorded_by: '', recorded_at: null, corrections: [], deviations: [], empty: true });
      return;
    }
    vals.forEach((v, vi) => {
      rows.push({
        ...base,
        ...(vi === 0 ? extras : { signatures: [], voided: [] }),
        key: `v-${st.instance_id}-${vi}`, first: vi === 0,
        label: v.label || '', value: v.value ?? '', unit: v.unit || '', limit: v.limit || '', verdict: v.verdict || '',
        recorded_by: v.recorded_by || '', recorded_at: v.recorded_at, corrections: v.corrections || [], deviations: [],
      });
    });
  });
  (op.readings || []).forEach((v, ri) => {
    rows.push({
      key: `r-${op.batch_config_id}-${ri}`, stepId: null, stepNo: `${opNo}.R${ri + 1}`, stepTitle: 'Reading', first: true,
      groupKey: op.batch_config_id, label: v.label || '', value: v.value ?? '', unit: v.unit || '', limit: v.limit || '',
      verdict: v.verdict || '', recorded_by: v.recorded_by || '', recorded_at: v.recorded_at, corrections: v.corrections || [],
      deviations: [], signatures: [], voided: [],
    });
  });
  const opDevs = [];
  (op.deviations || []).forEach((d) => {
    const named = rows.filter((r) => d.parameter && norm(r.label) === norm(d.parameter));
    const hit = named.find((r) => verdictKind(r.verdict) === 'out')
      || named.find((r) => d.actual && r.value && norm(d.actual).startsWith(norm(r.value)))
      || named[0];
    if (hit) hit.deviations.push(d); else opDevs.push(d);
  });
  rows.forEach((r) => {
    const k = verdictKind(r.verdict);
    r.flag = r.deviations.length ? 'deviation' : (k === 'out' ? 'out' : null);
  });
  return { key: op.batch_config_id, opNo, op, rows, opDevs };
});

const ResultTag = ({ row }) => {
  const k = verdictKind(row.verdict);
  if (row.deviations.length) {
    return (
      <Tooltip title={row.deviations.map(devText).join('\n')}>
        <Tag color="error" style={{ marginInlineEnd: 0 }}>Deviation</Tag>
      </Tooltip>
    );
  }
  if (k === 'out') {
    return (
      <Tooltip title={`Recorded as: ${capital(row.verdict)}`}>
        <Tag color="warning" style={{ marginInlineEnd: 0 }}>Out of limits</Tag>
      </Tooltip>
    );
  }
  if (k === 'within') return <Tag color="success" style={{ marginInlineEnd: 0 }}>Within limits</Tag>;
  if (k === 'other') return <Tag style={{ marginInlineEnd: 0 }}>{capital(row.verdict)}</Tag>;
  return null;
};
ResultTag.propTypes = { row: PropTypes.object.isRequired };

const Signatures = ({ list }) => {
  const { token } = theme.useToken();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {list.map((s, i) => (
        <Tooltip key={s.signature_record_id ?? i}
          title={joinDot([s.name, s.role, s.meaning, s.signed_at ? fmt(s.signed_at) : null, s.standing ? null : s.validity])}>
          <span style={{ fontSize: 12, lineHeight: '18px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <StatusIndicator state={s.standing ? 'ok' : 'error'} label={s.name || '—'} />
            {s.meaning ? <span style={{ color: token.colorTextSecondary }}> · {s.meaning}</span> : null}
          </span>
        </Tooltip>
      ))}
    </div>
  );
};
Signatures.propTypes = { list: PropTypes.array.isRequired };

const Notes = ({ row }) => {
  const { token } = theme.useToken();
  const line = { fontSize: 12.5, padding: '4px 0', borderTop: `1px solid ${token.colorBorderSecondary}`, overflowWrap: 'anywhere' };
  return (
    <Space size={4} wrap>
      {row.corrections.length > 0 && (
        <Popover
          trigger={['hover', 'click']}
          title={`${plural(row.corrections.length, 'correction')}${row.label ? ` · ${row.label}` : ''}`}
          content={(
            <div style={{ maxWidth: 360 }} data-testid="record-correction-popover">
              {row.corrections.map((c, i) => (
                <div key={i} style={i ? line : { ...line, borderTop: 'none' }}>
                  <div><b>{c.old || '—'}</b> → <b>{c.new || '—'}</b></div>
                  {c.reason ? <div>Reason: {c.reason}</div> : null}
                  {c.by || c.at ? <div style={{ color: token.colorTextSecondary }}>{joinDot([c.by, fmt(c.at)])}</div> : null}
                </div>
              ))}
            </div>
          )}
        >
          <Tag color="processing" style={{ cursor: 'pointer', marginInlineEnd: 0 }}>{plural(row.corrections.length, 'correction')}</Tag>
        </Popover>
      )}
      {row.voided.length > 0 && (
        <Popover
          trigger={['hover', 'click']}
          title={`${row.voided.length} voided ${row.voided.length === 1 ? 'entry' : 'entries'}`}
          content={(
            <div style={{ maxWidth: 360 }}>
              {row.voided.map((x, i) => (
                <div key={i} style={i ? line : { ...line, borderTop: 'none' }}>
                  <div>{x.label || '—'}{x.value ? ` (${x.value})` : ''}</div>
                  {x.reason ? <div>Reason: {x.reason}</div> : null}
                  {x.by || x.at ? <div style={{ color: token.colorTextSecondary }}>{joinDot([x.by, fmt(x.at)])}</div> : null}
                </div>
              ))}
            </div>
          )}
        >
          <Tag style={{ cursor: 'pointer', marginInlineEnd: 0 }}>{row.voided.length} voided</Tag>
        </Popover>
      )}
    </Space>
  );
};
Notes.propTypes = { row: PropTypes.object.isRequired };

const GroupHeader = ({ g, open, onToggle, shown }) => {
  const { token } = theme.useToken();
  const { op } = g;
  const sigs = op.counts?.signatures ?? 0;
  const flagged = g.rows.filter((r) => r.flag).length;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
      style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 12px', cursor: 'pointer', userSelect: 'none' }}
      data-testid="record-group"
    >
      {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
      <span style={{ fontWeight: 600, fontSize: 13.5, color: token.colorText }}>{g.opNo}. {op.operation_name || '—'}</span>
      {op.status ? <StatusIndicator state={statusState(op.status)} label={op.status} /> : null}
      <span style={{ fontSize: 12, color: token.colorTextSecondary }}>
        {fmt(op.started_at) || '—'} → {op.ended_at ? fmt(op.ended_at) : 'not ended'}
      </span>
      <Tag style={{ marginInlineEnd: 0 }}>{plural(sigs, 'signature')}</Tag>
      {flagged ? <Tag color="warning" style={{ marginInlineEnd: 0 }}>{flagged} flagged</Tag> : null}
      {g.opDevs.map((d, i) => (
        <Tooltip key={i} title={devText(d)}>
          <Tag color="error" style={{ marginInlineEnd: 0, whiteSpace: 'normal' }}>{d.type || 'Deviation'}</Tag>
        </Tooltip>
      ))}
      <span style={{ fontSize: 12, color: token.colorTextTertiary }}>
        {shown === g.rows.length ? plural(g.rows.length, 'row') : `${shown} of ${g.rows.length} rows`}
      </span>
    </div>
  );
};
GroupHeader.propTypes = { g: PropTypes.object.isRequired, open: PropTypes.bool.isRequired, onToggle: PropTypes.func.isRequired, shown: PropTypes.number.isRequired };

const BatchRecordTable = ({ operations, focusStep = null }) => {
  const { token } = theme.useToken();
  const tableRef = useRef(null);
  const boxRef = useRef(null);
  const [boxWidth, setBoxWidth] = useState(0);
  useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([e]) => setBoxWidth(Math.floor(e.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const groups = useMemo(() => buildGroups(operations), [operations]);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [query, setQuery] = useState('');
  const [onlyFlagged, setOnlyFlagged] = useState(false);
  const [height, setHeight] = useState(() => (typeof window === 'undefined' ? 600 : Math.max(420, window.innerHeight - 300)));

  useEffect(() => {
    const onResize = () => setHeight(Math.max(420, window.innerHeight - 300));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const flaggedTotal = useMemo(() => groups.reduce((n, g) => n + g.rows.filter((r) => r.flag).length, 0), [groups]);

  // a column only when some row has something for it — the card view's rule
  const has = useMemo(() => {
    const all = groups.flatMap((g) => g.rows);
    return {
      limit: all.some((r) => r.limit),
      result: all.some((r) => r.verdict || r.deviations.length),
      by: all.some((r) => r.recorded_by),
      at: all.some((r) => r.recorded_at),
      sig: all.some((r) => r.signatures.length),
      notes: all.some((r) => r.corrections.length || r.voided.length),
    };
  }, [groups]);

  const data = useMemo(() => {
    const q = norm(query);
    const out = [];
    groups.forEach((g) => {
      const rows = g.rows.filter((r) => (!onlyFlagged || r.flag)
        && (!q || norm(r.stepTitle).includes(q) || norm(r.label).includes(q) || norm(`${r.value} ${r.unit}`).includes(q)));
      const keepHeader = rows.length > 0 || (!q && !onlyFlagged) || (onlyFlagged && !q && g.opDevs.length > 0);
      if (!keepHeader) return;
      out.push({ key: `g-${g.key}`, isGroup: true, g, shown: rows.length });
      if (collapsed.has(g.key)) return;
      let prevStep;
      rows.forEach((r) => {
        out.push({ ...r, showStep: r.first || prevStep !== r.stepId });
        prevStep = r.stepId;
      });
    });
    return out;
  }, [groups, query, onlyFlagged, collapsed]);

  const toggle = (key) => setCollapsed((cur) => {
    const n = new Set(cur);
    if (n.has(key)) n.delete(key); else n.add(key);
    return n;
  });

  // bring a named step into view: open its group, clear filters that hide it, scroll to it
  const focusKey = useMemo(() => {
    if (focusStep == null) return null;
    for (const g of groups) {
      const r = g.rows.find((x) => x.stepId === focusStep);
      if (r) return { key: r.key, group: g.key };
    }
    return null;
  }, [groups, focusStep]);
  const pendingFocus = useRef(null);
  useEffect(() => {
    if (!focusKey) return;
    pendingFocus.current = focusKey.key;
    setCollapsed((cur) => {
      if (!cur.has(focusKey.group)) return cur;
      const n = new Set(cur);
      n.delete(focusKey.group);
      return n;
    });
    setQuery('');
    setOnlyFlagged(false);
  }, [focusKey]);
  useEffect(() => {
    const key = pendingFocus.current;
    if (!key || !data.some((r) => r.key === key)) return undefined;
    const t = setTimeout(() => {
      tableRef.current?.scrollTo?.({ key, align: 'top' });
      pendingFocus.current = null;
      // then the page: the table itself may sit below the fold
      setTimeout(() => document.getElementById(`record-step-${focusStep}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'center' }), 60);
    }, 60);
    return () => clearTimeout(t);
  }, [data, focusKey, focusStep]);

  const groupCell = (r) => (r.isGroup ? { colSpan: 0 } : {});
  const columns = [
    {
      title: '#', key: 'no', width: 56,
      render: (_, r) => (r.isGroup
        ? <GroupHeader g={r.g} open={!collapsed.has(r.g.key)} onToggle={() => toggle(r.g.key)} shown={r.shown} />
        : <span style={{ color: token.colorTextSecondary, fontSize: 12 }}>{r.showStep ? r.stepNo : ''}</span>),
    },
    {
      title: 'Step', key: 'step', width: 300, onCell: groupCell,
      render: (_, r) => (r.isGroup ? null : (
        <div style={{ overflowWrap: 'anywhere' }}>
          {r.showStep ? <div style={{ fontWeight: 500, color: token.colorText }}>{r.stepTitle || '—'}</div> : null}
          {r.label && !(r.showStep && norm(r.label) === norm(r.stepTitle))
            ? <div style={{ fontSize: 12, color: token.colorTextSecondary }}>{r.label}</div> : null}
          {r.empty ? <div style={{ fontSize: 12, color: token.colorTextTertiary }}>Nothing recorded on this step</div> : null}
        </div>
      )),
    },
    {
      title: 'Value recorded', key: 'value', width: 170, onCell: groupCell,
      render: (_, r) => (r.isGroup || r.empty ? null : (
        <span style={{ fontWeight: 600, color: token.colorText, overflowWrap: 'anywhere' }}>
          {r.value === '' ? '—' : r.value}{r.unit && !String(r.value).endsWith(r.unit) ? ` ${r.unit}` : ''}
        </span>
      )),
    },
    has.limit && {
      title: 'Expected / limits', key: 'limit', width: 130, onCell: groupCell,
      render: (_, r) => (r.isGroup ? null : <span style={{ color: token.colorTextSecondary }}>{r.limit}</span>),
    },
    has.result && { title: 'Result', key: 'result', width: 115, onCell: groupCell, render: (_, r) => (r.isGroup ? null : <ResultTag row={r} />) },
    has.by && { title: 'Recorded by', key: 'by', width: 130, onCell: groupCell, render: (_, r) => (r.isGroup ? null : r.recorded_by) },
    has.at && {
      title: 'Done at', key: 'at', width: 140, onCell: groupCell,
      render: (_, r) => (r.isGroup ? null : <span style={{ whiteSpace: 'nowrap', fontSize: 12.5 }}>{fmt(r.recorded_at)}</span>),
    },
    has.sig && {
      title: 'Checked / verified by', key: 'sig', width: 210, onCell: groupCell,
      render: (_, r) => (r.isGroup || !r.signatures.length ? null : <Signatures list={r.signatures} />),
    },
    has.notes && { title: 'Notes', key: 'notes', width: 130, onCell: groupCell, render: (_, r) => (r.isGroup ? null : <Notes row={r} />) },
  ].filter(Boolean);
  // fill the container when it is wider than the columns need (a virtual table does not stretch its columns)
  const need = columns.reduce((n, c) => n + c.width, 0);
  const grow = boxWidth > need ? (boxWidth - 2) / need : 1;
  if (grow > 1) columns.forEach((c) => { c.width = Math.floor(c.width * grow); });
  const scrollX = columns.reduce((n, c) => n + c.width, 0);
  // the group header spans every visible column
  columns[0].onCell = (r) => (r.isGroup ? { colSpan: columns.length, style: { background: token.colorFillTertiary } } : {});

  const rowClass = (r) => {
    if (r.isGroup) return 'rbe-rt-group';
    const cls = [];
    if (r.flag === 'deviation') cls.push('rbe-rt-dev');
    else if (r.flag === 'out') cls.push('rbe-rt-out');
    if (focusStep != null && r.stepId === focusStep) cls.push('rbe-rt-focus');
    return cls.join(' ');
  };

  // the step's first row carries the card view's anchor, so a caller finds the step in either layout
  const rowProps = (r) => (r.isGroup || r.stepId == null || !r.showStep ? {} : {
    id: `record-step-${r.stepId}`,
    'data-focused': focusStep != null && r.stepId === focusStep ? 'true' : undefined,
  });

  const allOpen = collapsed.size === 0;
  return (
    <div data-testid="batch-record-table" ref={boxRef}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <Input
          allowClear
          size="small"
          prefix={<Search size={13} />}
          placeholder="Search step or value"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ width: 260, maxWidth: '100%' }}
          data-testid="record-search"
        />
        <Space size={6}>
          <Switch size="small" checked={onlyFlagged} onChange={setOnlyFlagged} data-testid="record-only-flagged" />
          <span style={{ fontSize: 12.5, color: token.colorText }}>Show only flagged ({flaggedTotal})</span>
        </Space>
        <Space size={4}>
          <Tooltip title="Expand all">
            <Button size="small" aria-label="Expand all" icon={<ChevronsUpDown size={14} aria-hidden="true" />} disabled={allOpen}
              onClick={() => setCollapsed(new Set())} data-testid="record-expand-all" />
          </Tooltip>
          <Tooltip title="Collapse all">
            <Button size="small" aria-label="Collapse all" icon={<ChevronsDownUp size={14} aria-hidden="true" />}
              disabled={collapsed.size === groups.length} onClick={() => setCollapsed(new Set(groups.map((g) => g.key)))}
              data-testid="record-collapse-all" />
          </Tooltip>
        </Space>
      </div>
      {data.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={onlyFlagged && !query ? 'Nothing flagged on this record' : 'No rows match'} />
      ) : (
        <Table
          ref={tableRef}
          className="rbe-record-table"
          size="small"
          virtual
          rowKey="key"
          columns={columns}
          dataSource={data}
          pagination={false}
          scroll={{ x: scrollX, y: height }}
          rowClassName={rowClass}
          onRow={rowProps}
        />
      )}
      <style>{`
        .rbe-record-table .ant-table-thead > tr > th {
          background: ${token.colorFillQuaternary} !important; color: ${token.colorTextSecondary} !important;
          font-size: 11px !important; letter-spacing: 0.06em; text-transform: uppercase; font-weight: 700 !important;
        }
        .rbe-record-table .ant-table-tbody-virtual .ant-table-cell { border-bottom: 1px solid ${token.colorBorderSecondary}; font-size: 13px; }
        .rbe-record-table .rbe-rt-group .ant-table-cell { background: ${token.colorFillTertiary}; }
        .rbe-record-table .rbe-rt-out .ant-table-cell { background: ${token.colorWarningBg}; }
        .rbe-record-table .rbe-rt-dev .ant-table-cell { background: ${token.colorErrorBg}; }
        .rbe-record-table .rbe-rt-focus:not(.rbe-rt-out):not(.rbe-rt-dev) .ant-table-cell { background: ${token.colorPrimaryBg}; }
        .rbe-record-table .rbe-rt-focus .ant-table-cell:first-child { box-shadow: inset 4px 0 0 ${token.colorPrimary}; }
      `}</style>
    </div>
  );
};

BatchRecordTable.propTypes = { operations: PropTypes.array.isRequired, focusStep: PropTypes.number };

export default BatchRecordTable;
